import React, { useEffect, useState } from 'react';
import { Save, Settings2, Plus, X, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  getParametrosNomina,
  actualizarParametrosNomina,
} from '../nomina/Services/nomina.services';

const CAMPOS_GENERALES = [
  { key: 'divisorHora', label: 'Divisor del valor hora', ayuda: 'Valor hora = salario ÷ divisor. Legal: 210 (42 h/semana desde 15-jul-2026).', tipo: 'number', step: '1' },
  { key: 'horasBaseQuincenal', label: 'Horas base quincenales', ayuda: 'Por encima, las horas son extras.', tipo: 'number', step: '0.5' },
  { key: 'horasBaseMensual', label: 'Horas base mensuales', ayuda: 'Para nóminas mensuales y otros periodos (proporcional).', tipo: 'number', step: '0.5' },
  { key: 'jornadaDiariaHoras', label: 'Jornada diaria (ausencias)', ayuda: 'Horas de un día de incapacidad o permiso sin turno planificado.', tipo: 'number', step: '0.5' },
  { key: 'porcentajeIncapacidad', label: '% pago de incapacidad', ayuda: 'Porcentaje del valor hora con que se reconoce.', tipo: 'number', step: '0.01' },
  { key: 'auxilioTransporteMensual', label: 'Auxilio de transporte mensual', ayuda: 'Se paga proporcional al básico si salario + recargos + extras < 2 SMMLV.', tipo: 'number', step: '1' },
  { key: 'salarioMinimo', label: 'Salario mínimo (SMMLV)', ayuda: 'Define el tope del auxilio de transporte (2 SMMLV).', tipo: 'number', step: '1' },
  { key: 'inicioNocturno', label: 'Inicio horario nocturno', ayuda: 'Desde aquí aplica el recargo nocturno.', tipo: 'time' },
  { key: 'finNocturno', label: 'Fin horario nocturno', ayuda: 'Al día siguiente.', tipo: 'time' },
];

const CAMPOS_PORCENTAJE = [
  { key: 'recargoNocturnoPct', label: 'Recargo nocturno (%)', ayuda: 'Se paga solo el %, sobre horas dentro de la base. Legal: 35 %.' },
  { key: 'extraDiurnaPct', label: 'Extra diurna (%)', ayuda: 'Hora completa + %. Legal: 25 % (factor 1,25).' },
  { key: 'extraNocturnaPct', label: 'Extra nocturna (%)', ayuda: 'Hora completa + %. Legal: 75 % (factor 1,75).' },
];

// Costo empresa: aportes del empleador sobre el IBC y provisiones de prestaciones.
const CAMPOS_COSTO_EMPRESA = [
  { key: 'aportePensionPct', label: 'Pensión empleador (%)', ayuda: 'Sobre el IBC. Legal: 12 %.' },
  { key: 'aporteArlPct', label: 'ARL (%)', ayuda: 'Según la clase de riesgo: I 0,522 · II 1,044 · III 2,436 · IV 4,35 · V 6,96.' },
  { key: 'aporteCajaPct', label: 'Caja de compensación (%)', ayuda: 'Sobre el IBC. Legal: 4 %.' },
  { key: 'aporteSaludPct', label: 'Salud empleador (%)', ayuda: 'Legal: 8,5 %. Con la exoneración, solo desde 10 SMMLV.' },
  { key: 'aporteIcbfPct', label: 'ICBF (%)', ayuda: 'Legal: 3 %. Con la exoneración, solo desde 10 SMMLV.' },
  { key: 'aporteSenaPct', label: 'SENA (%)', ayuda: 'Legal: 2 %. Con la exoneración, solo desde 10 SMMLV.' },
  { key: 'provPrimaPct', label: 'Prima de servicios (%)', ayuda: 'Provisión sobre IBC + auxilio. Legal: 8,33 %.' },
  { key: 'provCesantiasPct', label: 'Cesantías (%)', ayuda: 'Provisión sobre IBC + auxilio. Legal: 8,33 %.' },
  { key: 'provInteresesPct', label: 'Intereses de cesantías (%)', ayuda: '12 % anual de las cesantías = 1 %.' },
  { key: 'provVacacionesPct', label: 'Vacaciones (%)', ayuda: 'Provisión sobre el básico. Legal: 4,17 %.' },
];

const CAMPOS = [
  ...CAMPOS_GENERALES,
  ...CAMPOS_PORCENTAJE.map((c) => ({ ...c, tipo: 'number' })),
  ...CAMPOS_COSTO_EMPRESA.map((c) => ({ ...c, tipo: 'number' })),
];

// Tipos de recargo y extra con su porcentaje calculado; se pueden fijar a mano.
const FACTORES = [
  { key: 'recargoNocturno', label: 'Recargo nocturno (día ordinario)', soloRecargo: true, calculo: (p) => p('recargoNocturnoPct') },
  { key: 'recargoDominicalDiurno', label: 'Dominical diurno', calculo: (p, dom) => dom },
  { key: 'recargoDominicalNocturno', label: 'Dominical nocturno', calculo: (p, dom) => p('recargoNocturnoPct') + dom },
  { key: 'recargoFestivoDiurno', label: 'Festivo diurno', calculo: (p, dom) => dom },
  { key: 'recargoFestivoNocturno', label: 'Festivo nocturno', calculo: (p, dom) => p('recargoNocturnoPct') + dom },
  { key: 'extraDiurna', label: 'Extra diurna', calculo: (p) => p('extraDiurnaPct') },
  { key: 'extraNocturna', label: 'Extra nocturna', calculo: (p) => p('extraNocturnaPct') },
  { key: 'extraDominicalDiurna', label: 'Extra dominical diurna', calculo: (p, dom) => p('extraDiurnaPct') + dom },
  { key: 'extraDominicalNocturna', label: 'Extra dominical nocturna', calculo: (p, dom) => p('extraNocturnaPct') + dom },
  { key: 'extraFestivaDiurna', label: 'Extra festiva diurna', calculo: (p, dom) => p('extraDiurnaPct') + dom },
  { key: 'extraFestivaNocturna', label: 'Extra festiva nocturna', calculo: (p, dom) => p('extraNocturnaPct') + dom },
];

const limpiarFactores = (f = {}) =>
  Object.fromEntries(Object.entries(f).filter(([, v]) => v !== '' && v !== null && v !== undefined).map(([k, v]) => [k, Number(v)]));

const mismosTramos = (a = [], b = []) =>
  JSON.stringify(a.map((t) => [t.desde, Number(t.pct)])) === JSON.stringify(b.map((t) => [t.desde, Number(t.pct)]));

/** Parámetros de la nómina por periodo (documento único en el backend). */
const ParametrosNominaCard = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [original, setOriginal] = useState(null);
  const [form, setForm] = useState({});
  const [tramos, setTramos] = useState([]);
  const [factores, setFactores] = useState({});
  const [guardando, setGuardando] = useState(false);

  const aplicar = (p) => {
    setOriginal(p);
    setForm(p || {});
    setTramos((p?.recargoDominicalTramos || []).map((t) => ({ desde: t.desde, pct: t.pct })));
    setFactores({ ...(p?.factoresPersonalizados || {}) });
  };

  useEffect(() => {
    getParametrosNomina()
      .then(aplicar)
      .catch((e) => toast({ title: 'Error', description: e.message, variant: 'destructive' }));
  }, []);

  const cambiados = CAMPOS.filter((c) => original && String(form[c.key] ?? '') !== String(original[c.key] ?? ''));
  const tramosCambiados = original && !mismosTramos(tramos, original.recargoDominicalTramos || []);
  const exoneracionCambiada = original && !!form.exoneracion114 !== !!original.exoneracion114;
  const ibcCambiado = original && !!form.ibcSinRecargosExtras !== !!original.ibcSinRecargosExtras;
  const factoresCambiados =
    original && JSON.stringify(limpiarFactores(factores)) !== JSON.stringify(limpiarFactores(original.factoresPersonalizados));
  const hayCambios = cambiados.length > 0 || tramosCambiados || exoneracionCambiada || factoresCambiados || ibcCambiado;

  // Recargo dominical vigente hoy, para mostrar los factores resultantes.
  const hoy = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
  const domHoy = [...tramos].sort((a, b) => a.desde.localeCompare(b.desde)).reduce((p, t) => (t.desde <= hoy ? Number(t.pct) : p), Number(tramos[0]?.pct) || 0);
  const pct = (k) => Number(form[k]) || 0;
  const factor = (n) => (1 + n / 100).toLocaleString('es-CO', { maximumFractionDigits: 2 });

  const guardar = async () => {
    setGuardando(true);
    try {
      const payload = Object.fromEntries(
        cambiados.map((c) => [c.key, c.tipo === 'number' ? Number(form[c.key]) : form[c.key]]),
      );
      if (exoneracionCambiada) payload.exoneracion114 = !!form.exoneracion114;
      if (ibcCambiado) payload.ibcSinRecargosExtras = !!form.ibcSinRecargosExtras;
      if (factoresCambiados) payload.factoresPersonalizados = limpiarFactores(factores);
      if (tramosCambiados) payload.recargoDominicalTramos = tramos.map((t) => ({ desde: t.desde, pct: Number(t.pct) }));
      const p = await actualizarParametrosNomina({
        ...payload,
        actualizadoPor: { id: user?._id || user?.id, nombre: user?.nombre || user?.name || user?.email },
      });
      aplicar(p);
      toast({ title: 'Guardado', description: 'Aplica a las nóminas que se generen o recalculen desde ahora.' });
    } catch (e) {
      toast({ title: 'No se guardó', description: e.message, variant: 'destructive' });
    } finally {
      setGuardando(false);
    }
  };

  const campo = (c) => (
    <div key={c.key} className="space-y-1">
      <Label htmlFor={`pn-${c.key}`} className="text-xs">{c.label}</Label>
      <Input
        id={`pn-${c.key}`}
        type={c.tipo || 'number'}
        step={c.tipo === 'time' ? undefined : c.step || '0.01'}
        min={(c.tipo || 'number') === 'number' ? 0 : undefined}
        value={form[c.key] ?? ''}
        onChange={(e) => setForm({ ...form, [c.key]: e.target.value })}
        className={cambiados.some((x) => x.key === c.key) ? 'border-primary' : ''}
      />
      <p className="text-[11px] text-muted-foreground">{c.ayuda}</p>
    </div>
  );

  return (
    <div className="rounded-lg border bg-card p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-md bg-primary/15">
            <Settings2 className="h-4 w-4 text-primary" />
          </div>
          <div>
            <p className="font-medium text-sm">Parámetros de nómina</p>
            <p className="text-xs text-muted-foreground">
              Los usa la nómina por periodo. Valores legales de referencia: validarlos con el asesor de nómina.
            </p>
          </div>
        </div>
        <Button size="sm" onClick={guardar} disabled={!hayCambios || guardando}>
          <Save className="mr-2 h-4 w-4" /> Guardar parámetros
        </Button>
      </div>
      {!original ? (
        <p className="text-sm text-muted-foreground">Cargando...</p>
      ) : (
        <>
          {Number(original.divisorHora) !== 210 && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
              <Info className="h-4 w-4 shrink-0" />
              <span>
                El divisor está en {original.divisorHora}. Con la jornada de 42 h semanales (Ley 2101, desde el
                15-jul-2026) la hora ordinaria es salario ÷ 210; antes era ÷ 220.
              </span>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">{CAMPOS_GENERALES.map(campo)}</div>

          <div className="border-t pt-4 space-y-3">
            <p className="text-sm font-medium">Recargos y extras</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">{CAMPOS_PORCENTAJE.map(campo)}</div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Recargo dominical y festivo por vigencia (Ley 2466 de 2025)</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setTramos([...tramos, { desde: hoy, pct: domHoy }])}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" /> Tramo
                  </Button>
                </div>
                {tramos.map((t, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-12">Desde</span>
                    <Input
                      type="date"
                      value={t.desde}
                      onChange={(e) => setTramos(tramos.map((x, j) => (j === i ? { ...x, desde: e.target.value } : x)))}
                      className="w-40"
                    />
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={t.pct}
                      onChange={(e) => setTramos(tramos.map((x, j) => (j === i ? { ...x, pct: e.target.value } : x)))}
                      className="w-24"
                    />
                    <span className="text-xs text-muted-foreground">%</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      disabled={tramos.length <= 1}
                      onClick={() => setTramos(tramos.filter((_, j) => j !== i))}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <p className="text-[11px] text-muted-foreground">
                  Cada hora en domingo o festivo usa el % vigente en su fecha; una quincena que cruza un cambio aplica
                  ambos.
                </p>
              </div>

              <div className="rounded-md border p-3 text-xs">
                <p className="font-medium text-sm">Porcentajes que aplica la nómina</p>
                <table className="w-full">
                  <thead>
                    <tr className="text-muted-foreground text-left">
                      <th className="py-1 font-normal">Tipo</th>
                      <th className="py-1 font-normal text-right">Calculado</th>
                      <th className="py-1 font-normal text-right">Personalizado (%)</th>
                      <th className="py-1 font-normal text-right">Se paga</th>
                    </tr>
                  </thead>
                  <tbody>
                    {FACTORES.map((f) => {
                      const calculado = f.calculo(pct, domHoy);
                      const valor = factores[f.key];
                      const efectivo = valor === '' || valor === undefined || valor === null ? calculado : Number(valor);
                      return (
                        <tr key={f.key} className="border-t">
                          <td className="py-1 pr-2">{f.label}</td>
                          <td className="py-1 text-right tabular-nums text-muted-foreground">{calculado} %</td>
                          <td className="py-1 text-right">
                            <Input
                              type="number"
                              min="0"
                              max="300"
                              step="0.5"
                              value={valor ?? ''}
                              placeholder={String(calculado)}
                              onChange={(e) => setFactores({ ...factores, [f.key]: e.target.value })}
                              className={`h-7 w-20 ml-auto text-right ${valor !== '' && valor !== undefined && valor !== null ? 'border-primary' : ''}`}
                            />
                          </td>
                          <td className="py-1 text-right tabular-nums font-medium">
                            {f.soloRecargo ? `+${efectivo} %` : `× ${factor(efectivo)}`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="text-muted-foreground pt-2">
                  Recargo nocturno en día ordinario: la hora ya está en el básico y se paga solo el %. Domingo y festivo:
                  hora completa + %. Extras: hora completa + %. Aplica al generar o recalcular la nómina.
                </p>
              </div>
            </div>
          </div>

          <div className="border-t pt-4 space-y-3">
            <div>
              <p className="text-sm font-medium">Costo empresa: aportes y prestaciones</p>
              <p className="text-[11px] text-muted-foreground">
                Se suman al devengado para el costo total de la nómina. Aportes sobre el IBC (básico + recargos + extras
                + incapacidad); prestaciones como provisión mensual.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">{CAMPOS_COSTO_EMPRESA.map(campo)}</div>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={!!form.ibcSinRecargosExtras}
                onChange={(e) => setForm({ ...form, ibcSinRecargosExtras: e.target.checked })}
              />
              <span>
                <b>Excepción: el IBC y las prestaciones no incluyen recargos ni extras.</b> Salud y pensión de la persona
                (4 % + 4 %) y los aportes de la empresa (pensión, ARL, caja) se calculan sobre básico + incapacidad; prima,
                cesantías e intereses sobre básico + incapacidad + auxilio de transporte.{' '}
                <span className="text-amber-700 dark:text-amber-300">
                  La norma incluye los recargos y extras habituales en el IBC y en las prestaciones (art. 127 C.S.T.):
                  confirmarlo con el asesor.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={!!form.exoneracion114}
                onChange={(e) => setForm({ ...form, exoneracion114: e.target.checked })}
              />
              <span>
                Exoneración art. 114-1 E.T.: la empresa no paga salud, ICBF ni SENA por quienes ganan menos de 10 SMMLV
                (aplica a personas jurídicas declarantes de renta; confirmarlo con el contador).
              </span>
            </label>
          </div>
        </>
      )}
    </div>
  );
};

export default ParametrosNominaCard;
